import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { PageTitle } from '../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FileDownloadRounded, PlaylistAdd } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import { SearchField } from '../../components/inputs/SearchField';
import { JournalsTable } from './components/JournalsTable';
import { MasterListJournalModal } from './components/MasterListJournalModal';
import {
  MaterialListStatus,
  MasterListJournal,
  MasterListJournalRequestBody,
} from '../../types/MasterList.types';
import { AlertDialog } from '../../components/AlertDialog';
import {
  useDeleteMasterListChangeset,
  useExportMasterList,
  useGetMasterList,
  useMasterListStatus,
  useRevertMasterList,
  useUpdateMasterList,
  useUpdateMasterListChangeset,
  useVerifyMasterList,
} from '../../hooks/api/useMasterList';
import { useMasterListFilters } from './hooks/useMasterListFilters';
import { AlertMessage } from '../../components/AlertMessage';

export function MasterList() {
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
  const [isApplyChangesModalOpen, setIsApplyChangesModalOpen] = useState(false);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [applyChangesError, setApplyChangesError] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState<
    MasterListJournal | undefined
  >(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();
  const { masterList, isMasterListLoading } = useGetMasterList();
  const exportMasterList = useExportMasterList();
  const { hasPendingChanges } = useMasterListStatus();
  const { updateMasterList } = useUpdateMasterList();
  const { revertMasterList } = useRevertMasterList();
  const { updateMasterListChangeset } = useUpdateMasterListChangeset();
  const { deleteChangeset } = useDeleteMasterListChangeset();
  const { verifyMasterList } = useVerifyMasterList();
  const {
    availableFilters,
    selectedFilters,
    hasFiltersApplied,
    filteredJournals,
    clearAllFilters,
    handleToggleFilter,
  } = useMasterListFilters({
    journals: masterList,
    searchTerm,
  });
  const handleAddJournal = async (
    journal: MasterListJournal,
    changeMessage: string
  ) => {
    await updateMasterList({
      action: 'added',
      delta: {
        name: journal.name,
        publisher: journal.publisher,
        imprint: journal.imprint,
        licenses: journal.licenses,
        publishing_model: journal.publishing_model,
        issn_e: journal.issn_e,
        issn_p: journal.issn_p,
        subjects: journal.subjects,
        url: journal.url,
      },
      change_message: changeMessage,
    });
  };
  const handleEditJournal = async (
    journalId: string,
    delta: MasterListJournalRequestBody['delta'],
    changeMessage: string
  ) => {
    if (selectedJournal?.status === MaterialListStatus.added) {
      const { ...journalFields } = selectedJournal;
      await updateMasterListChangeset({
        itemId: journalId,
        delta: { ...journalFields, ...delta },
        change_message: changeMessage,
      });
    } else {
      await updateMasterList({
        action: 'updated',
        id: journalId,
        delta,
        change_message: changeMessage,
      });
    }
  };
  const handleOpenCreateModal = () => {
    setSelectedJournal(undefined);
    setIsJournalModalOpen(true);
  };
  const handleOpenEditModal = (journal: MasterListJournal) => {
    setSelectedJournal(journal);
    setIsJournalModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsJournalModalOpen(false);
    setSelectedJournal(undefined);
  };
  const handleRevertJournal = async (
    journalId: string,
    status: MaterialListStatus | null | undefined
  ) => {
    if (status === MaterialListStatus.added) {
      await deleteChangeset(journalId);
    } else {
      await revertMasterList(journalId);
    }
  };
  const handleCeaseJournal = async (
    journalId: string,
    changeMessage: string
  ) => {
    await updateMasterList({
      action: 'deleted',
      id: journalId,
      change_message: changeMessage,
    });
  };
  const handleApplyChanges = () => {
    setApplyChangesError(false);
    setIsApplyChangesModalOpen(true);
  };
  const handleConfirmApplyChanges = async () => {
    setApplyChangesError(false);
    setIsApplyingChanges(true);
    try {
      await verifyMasterList();
      setIsApplyChangesModalOpen(false);
    } catch {
      setApplyChangesError(true);
    } finally {
      setIsApplyingChanges(false);
    }
  };

  const exportEffectiveMasterList = useCallback(
    () =>
      exportMasterList({
        view: 'effective',
        search: searchTerm,
        publishers: selectedFilters.publishers,
        imprints: selectedFilters.imprints,
        publishingModels: selectedFilters.publishingModels,
        statuses: selectedFilters.statuses,
        licenses: selectedFilters.licenses,
      }),
    [exportMasterList, searchTerm, selectedFilters]
  );

  return (
    <>
      <PageTitle title={t('masterList.title') as string} />
      <Card variant="default" sx={{ mb: 24 }}>
        <Grid container spacing={24}>
          <Grid item md={12} xs={12}>
            <Typography component="h1" variant="h4" gutterBottom>
              {t('masterList.title')}
            </Typography>
            <Typography variant="body1">
              {t('masterList.description')}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: { xs: 2, sm: 3 },
                columnGap: { xs: 2, md: 4 },
                rowGap: 2,
              }}
            >
              <Box
                sx={{
                  flex: '1 1 280px',
                  minWidth: 0,
                  maxWidth: { sm: 520 },
                }}
              >
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={t('masterList.searchLabel') as string}
                  ariaLabel={t('masterList.searchAriaLabel') as string}
                  helperText={
                    filteredJournals.length === masterList.length ? (
                      <>
                        {t('masterList.showingJournals', {
                          count: masterList.length,
                        })}
                      </>
                    ) : (
                      <>
                        {t('masterList.showingJournalsPartial', {
                          visible: filteredJournals.length,
                          total: masterList.length,
                        })}
                      </>
                    )
                  }
                  width={500}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 8,
                  flex: '0 1 auto',
                }}
              >
                <Button
                  variant="secondary"
                  endIcon={<FileDownloadRounded />}
                  onClick={exportEffectiveMasterList}
                >
                  {t('masterList.exportButton')}
                </Button>
                <Button
                  variant="primary"
                  endIcon={<PlaylistAdd />}
                  onClick={handleOpenCreateModal}
                >
                  {t('masterList.addJournalButton')}
                </Button>
                <Button
                  variant="default"
                  endIcon={<SaveIcon />}
                  onClick={handleApplyChanges}
                  disabled={!hasPendingChanges}
                >
                  {t('masterList.applyChangesButton')}
                </Button>
              </Box>
            </Box>
            {hasFiltersApplied && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="default"
                  startIcon={<ClearIcon />}
                  onClick={clearAllFilters}
                  sx={{ width: 'fit-content' }}
                >
                  {t('masterList.clearAllFilters')}
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Card>
      <JournalsTable
        journals={filteredJournals}
        filters={availableFilters}
        selectedFilters={selectedFilters}
        hasFiltersApplied={hasFiltersApplied}
        isLoading={isMasterListLoading}
        onToggleFilter={handleToggleFilter}
        onJournalClick={handleOpenEditModal}
      />
      <MasterListJournalModal
        open={isJournalModalOpen}
        onClose={handleCloseModal}
        onCreate={handleAddJournal}
        onUpdate={handleEditJournal}
        onRevert={handleRevertJournal}
        onCease={handleCeaseJournal}
        journal={selectedJournal}
        editMode={!!selectedJournal}
      />
      <AlertDialog
        open={isApplyChangesModalOpen}
        variant="default"
        onClose={() => setIsApplyChangesModalOpen(false)}
        title={t('masterList.applyChangesTitle')}
        description={
          <>
            {t('masterList.applyChangesDescription')}
            <AlertMessage severity="warning" className="mt-4">
              {t('masterList.alertApplyChangesDescription')}
            </AlertMessage>
          </>
        }
        actions={{
          cancel: {
            label: 'Cancel',
            onClick: () => setIsApplyChangesModalOpen(false),
          },
          confirm: {
            label: 'Confirm changes',
            onClick: handleConfirmApplyChanges,
            loading: isApplyingChanges,
            loadingLabel: t('masterList.applyingChanges'),
          },
        }}
      >
        {applyChangesError && (
          <AlertMessage severity="error" className="mt-4">
            {t('masterList.applyChangesError')}
          </AlertMessage>
        )}
      </AlertDialog>
    </>
  );
}
